import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatRadioChange } from "@angular/material/radio";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { distinctUntilChanged, map, take } from "rxjs/operators";
import {
  Phone,
  PhoneService
} from "../../../../../common/services/api/resources/phone/phone.service";
import { ParameterValue } from "../../../../../common/services/api/util/api-action-description";
import { SnackbarService } from "../../../shared/components/snackbar/snackbar.service";
import {
  getModelFeatures,
  PhoneFeatures,
  PhoneMake
} from "../../../../../common/interfaces/phone-info";
import { PhoneItemInfo } from "../phone-admin-page.component";
import { LineInfo } from "../phoneDetail/phone-detail.component";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";
import { ApiPromiseSubject } from "@onsip/common/services/api/api-promise-state.service";

export interface LineOptionsType {
  /** type of button options for lines: both means line can be switch between phone and blf */
  type: "phone" | "blf" | "both";
  /** expansion means it is a line from the expansion modules */
  isExpansion: boolean;
}

@Component({
  selector: "onsip-phone-lines-edit",
  templateUrl: "./phone-lines-edit.component.html",
  styleUrls: ["./phone-lines-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhoneLinesEditComponent implements OnInit, OnDestroy {
  @Input() phoneInfo!: PhoneItemInfo;
  @Input() lines: Array<LineInfo> = [];
  @Input() isUsingExpansionModule = false;
  @Output() viewPanel = new EventEmitter<void>();

  formGroup = this.fb.group({
    expansionModule: this.fb.control(false),
    linesInfo: this.fb.group({})
  });

  loadingStateComplete$ = new BehaviorSubject(true);

  lessThanMediumBreakpoint = this.breakpointObserver
    .observe([Breakpoints.Small, Breakpoints.XSmall])
    .pipe(map(result => result.matches));

  /** name of expansion module - use to determine if radio selection buttons should be shown or not */
  expansionModuleName = "";

  lineOptions: Array<LineOptionsType> = [];

  phoneFeatures!: PhoneFeatures;
  /** flag to track whether module has been added already - to prevent multiple module addition */
  addedExpansionModule = false;
  /** flag to track whether module has been removed already - prevent multiple module removals */
  removedExpansionModule = true;
  /** track number of total lines being used, including expansion modules */
  private totalLines = 0;
  private firstLineAssigned = false;
  private unsubscriber: Subscription = new Subscription();

  constructor(
    private fb: NonNullableFormBuilder,
    private phoneService: PhoneService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit() {
    this.phoneFeatures = getModelFeatures(this.phoneInfo.make as PhoneMake, this.phoneInfo.model);

    if (this.phoneFeatures.SideCar) this.expansionModuleName = this.phoneFeatures.SideCar[0].Type;

    this.formGroup.setValue({
      expansionModule: this.isUsingExpansionModule,
      linesInfo: {}
    });

    // create formControls
    this.getDefaultLinesFormControl();
    if (this.isUsingExpansionModule) this.addExpansionFormControl();

    this.unsubscriber.add(
      this.formGroup.controls.expansionModule.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(value => {
          if (value && !this.addedExpansionModule) this.addExpansionFormControl();
          if (!value && !this.removedExpansionModule) this.removeExpansionFormControls();
        })
    );

    this.getFormControlErrorState();

    this.unsubscriber.add(
      this.formGroup.valueChanges.subscribe(() => {
        this.checkDuplicateLinesFormGroup();
        this.getFormControlErrorState();
        if (
          this.linesInfo.controls.nameLine1 &&
          this.linesInfo.controls.nameLine1.value &&
          !this.firstLineAssigned
        ) {
          this.firstLineAssigned = true;
          this.setLinesEnabled();
        } else if (this.firstLineAssigned && this.allLinesUnassigned()) {
          this.firstLineAssigned = false;
          this.setLinesDisabled();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  get isPolycomPhone() {
    return this.phoneInfo.make === PhoneMake.Polycom;
  }

  get linesInfo(): FormGroup {
    return this.formGroup.controls.linesInfo as FormGroup;
  }

  getLinesControl(index: number) {
    return this.linesInfo.controls[`callWaitingLine${index}`];
  }

  toggleLinePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  getLineName(index: number): string {
    // index in the form field starts at 1 instead of 0 so we need the + 1
    const type = this.linesInfo.controls[`typeLine${index + 1}`].value;

    return type === "Phone line" ? `Line ${index + 1}` : `BLF ${index + 1}`;
  }

  handleLineChange(event: MatRadioChange, index: number): void {
    const callWaitingControl = this.linesInfo.controls[`callWaitingLine${index + 1}`];
    if (event.value === "Busy lamp") {
      callWaitingControl.disable();
    } else if (event.value === "Phone line") {
      callWaitingControl.enable();
      callWaitingControl.setValue(callWaitingControl.value);
    }
  }

  /** get error message for the first line */
  getLineError(index: number): string {
    if (
      this.linesInfo.controls[`nameLine${index}`] &&
      this.linesInfo.controls[`nameLine${index}`].hasError("duplicateKey")
    ) {
      return "This user is already assigned to a line of this type.";
    }
    if (
      this.linesInfo.controls.nameLine1 &&
      this.linesInfo.controls.nameLine1.hasError("required")
    ) {
      return "A user must be assigned to Line 1";
    }
    return "";
  }

  submit(): void {
    if (this.formGroup.valid) {
      this.loadingStateComplete$.next(false);
      const parsedLinesObject: { [key: string]: Partial<LineInfo> } = {};
      const formGroupObject = this.linesInfo.getRawValue();
      Object.entries(formGroupObject).forEach(([key, value]) => {
        const line = key.replace(/\D/g, "");
        if (!parsedLinesObject[line]) parsedLinesObject[line] = { line };
        if (key.includes("name")) {
          parsedLinesObject[line] = {
            ...parsedLinesObject[line],
            ...{ aor: value as LineInfo["aor"] }
          };
        }
        if (key.includes("type")) {
          parsedLinesObject[line] = {
            ...parsedLinesObject[line],
            ...{ type: value as LineInfo["type"] }
          };
        }
        if (key.includes("callWaiting")) {
          parsedLinesObject[line] = {
            ...parsedLinesObject[line],
            ...{
              enabled: (this.isPolycomPhone && value && !this.linesInfo.controls[key].disabled
                ? "enabled"
                : "disabled") as LineInfo["enabled"]
            }
          };
        }
      });

      const assignedLines = Object.values(parsedLinesObject).filter(lines => lines.aor !== "");
      // check the number of lines are assigned. If number is zero, then we want to run phoneDeassignAddress because
      // phoneAssignAddressBulk requires at least one assigned address
      if (!assignedLines.length) {
        this.phoneService
          .phoneDeassignAddress({ PhoneId: this.phoneInfo.phoneId })
          .then(result => this.handleAssignAddressResult(result));
      } else {
        const formatParams: Record<string, ParameterValue> = {};
        Object.values(assignedLines).forEach((lines, index) => {
          formatParams[`Addresses[${index}][Line]`] = lines.line;
          formatParams[`Addresses[${index}][Address]`] = lines.aor;
          if (lines.type === "Phone line") {
            formatParams[`Addresses[${index}][CallsPerLine]`] = lines.enabled === "enabled" ? 0 : 1;
          } else if (lines.type === "Busy lamp") {
            formatParams[`Addresses[${index}][BusyLampField]`] = true;
            formatParams[`Addresses[${index}][SubscriberLine]`] = 1;
          }
        });

        this.phoneService
          .phoneAssignAddressBulk(this.phoneInfo.phoneId, formatParams)
          .then(result => this.handleAssignAddressResult(result));
      }
    }
  }

  private handleAssignAddressResult(result: ApiPromiseSubject<Phone>): void {
    if (result.status === "success") {
      this.snackbar.openSnackBar(
        this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
        "success"
      );
      this.viewPanel.emit();
    } else {
      this.snackbar.openSnackBar(result.data.message, "error");
      this.phoneService.clearErrors();
    }
    this.loadingStateComplete$.next(true);
  }

  /** generate formControls for lines that comes with phone minus expansion
   * nameFormControl - holds the user address the line is assigned to
   * typeFormControl - whether or not type is phone line or blf
   * callWaitingFormControl - whether or not call waiting is enabled - only if line is for phone
   */
  private getDefaultLinesFormControl(): void {
    // line can be either blf or phone if overlap is true
    const blfOverlap = this.phoneFeatures.BLFOverlap || false;
    // if overlap is true than total number of line is the maximum of Lines and BLF...
    // however when blf is greater than Lines - the total is blf + 1. That seems wrong in the old admin portal but i will leave that here for now
    // but if overlap is false, we need to add the number of Lines with BLF
    const totalLines =
      blfOverlap && this.phoneFeatures.BLF
        ? this.phoneFeatures.Lines < this.phoneFeatures.BLF
          ? this.phoneFeatures.BLF + 1
          : this.phoneFeatures.Lines
        : this.phoneFeatures.Lines + (this.phoneFeatures.BLF || 0);

    // to replace the instance variable, linesOptions, at the class level after loop is finished
    const togglableOptions: Array<"phone" | "blf" | "both"> = [];

    // generate formcontrol for lines
    for (let i = 1; i <= totalLines; i++) {
      if (i === 1) {
        // First Line will always be a phone line - following the old admin portal
        togglableOptions.push("phone");
      } else {
        // if blf is overlappable
        if (blfOverlap && this.phoneFeatures.BLF) {
          // following the old admin portal structure - phone only lines are at the top
          if (this.phoneFeatures.Lines < this.phoneFeatures.BLF) {
            // if phoneLines are less than blf than all lines less than phoneLines can be both
            if (i <= this.phoneFeatures.Lines) {
              togglableOptions.push("both");
            } else {
              togglableOptions.push("blf");
            }
          } else {
            // vice versa if blf is less than phonelines than the difference will give us phone only lines
            if (i <= this.phoneFeatures.Lines - this.phoneFeatures.BLF) {
              togglableOptions.push("phone");
            } else {
              togglableOptions.push("both");
            }
          }
        } else {
          // following the old admin portal structure - phone lines go before blf lines
          if (i <= this.phoneFeatures.Lines) {
            togglableOptions.push("phone");
          } else {
            togglableOptions.push("blf");
          }
        }
      }
      // we will use the option to determine the default value of the type of line
      // phone and both will default to phone, blf will default to blf
      const option = togglableOptions[togglableOptions.length - 1];
      this.generateFormControl(i, option);
    }
    // update instance variables
    this.lineOptions = togglableOptions.map(type => ({ type, isExpansion: false }));
    this.totalLines = totalLines;
  }

  private addExpansionFormControl(): void {
    if (this.phoneFeatures.SideCar) {
      // expansion module contains only blf lines so we can just fill in lineOptions with blf
      const expansionModuleLength =
        this.phoneFeatures.SideCar[0].BLF * this.phoneFeatures.SideCar[0].Multiple;
      for (let i = this.totalLines + 1; i <= this.totalLines + expansionModuleLength; i++) {
        this.generateFormControl(i, "blf");
      }
      // update instance variables
      this.lineOptions = this.lineOptions.concat(
        Array.from(Array(expansionModuleLength), () => ({ type: "blf", isExpansion: true }))
      );
      this.totalLines += expansionModuleLength;
      this.addedExpansionModule = true;
      this.removedExpansionModule = false;
    }
  }

  private generateFormControl(i: number, option: "phone" | "blf" | "both"): void {
    const existingInfo = this.lines.find(line => line.line === i.toString());
    // disable lines if it is not the first line and first line is not assigned

    const disabled = i > 1 && !this.firstLineAssigned;
    this.linesInfo.addControl(
      `nameLine${i}`,
      new FormControl({ value: existingInfo?.aor || "", disabled: disabled })
    );
    this.setFirstLineAssigned(i);
    this.linesInfo.addControl(
      `typeLine${i}`,
      new FormControl({
        value: existingInfo?.type || (option === "blf" ? "Busy lamp" : "Phone line"),
        disabled: disabled
      })
    );
    this.linesInfo.addControl(
      `callWaitingLine${i}`,
      new FormControl({
        value: existingInfo?.enabled
          ? existingInfo.enabled === "enabled"
          : this.phoneFeatures.Concurrent,
        disabled: disabled || existingInfo?.enabled === "*disabled"
      })
    );
  }

  private removeExpansionFormControls(): void {
    if (this.phoneFeatures.SideCar) {
      const expansionModuleLength =
        this.phoneFeatures.SideCar[0].BLF * this.phoneFeatures.SideCar[0].Multiple;
      for (let i = this.totalLines; i > this.totalLines - expansionModuleLength; i--) {
        this.deleteExpansionFormControl(i);
      }
      // update instance variables
      this.totalLines -= expansionModuleLength;
      this.lineOptions = this.lineOptions.slice(0, this.totalLines);
      this.removedExpansionModule = true;
      this.addedExpansionModule = false;
    }
  }

  private deleteExpansionFormControl(i: number): void {
    this.linesInfo.removeControl(`nameLine${i}`);
    this.linesInfo.removeControl(`typeLine${i}`);
    this.linesInfo.removeControl(`callWaitingLine${i}`);
  }

  /** make sure the first line is assigned if any other line is also assigned
   * form will throw error on the first line if this is not true
   * however, if all lines are unassigned, first line is no longer required to be assigned
   */
  private getFormControlErrorState(): void {
    const allUnassigned = this.allLinesUnassigned();
    if (this.linesInfo.controls.nameLine1 && !allUnassigned) {
      this.linesInfo.controls.nameLine1.setValidators(Validators.required);
      if (!this.linesInfo.controls.nameLine1.value) {
        this.linesInfo.controls.nameLine1.setErrors({ required: true });
        this.linesInfo.controls.nameLine1.markAsTouched();
      }
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    } else if (this.linesInfo.controls.nameLine1 && allUnassigned) {
      // eslint-disable-next-line no-null/no-null
      this.linesInfo.controls.nameLine1.setErrors(null);
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    } else {
      this.formGroup.clearValidators();
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    }
  }

  private setLinesEnabled(): void {
    for (let i = 1; i < this.totalLines + 1; i++) {
      if (this.linesInfo.controls[`nameLine${i}`]) {
        this.linesInfo.controls[`nameLine${i}`].enable();
        this.linesInfo.controls[`typeLine${i}`].enable();
        if (this.phoneFeatures.Concurrent) {
          this.linesInfo.controls[`callWaitingLine${i}`].enable();
        }
      }
    }
  }

  // disable lines > 1
  private setLinesDisabled(): void {
    for (let i = 2; i < this.totalLines + 1; i++) {
      if (this.linesInfo.controls[`nameLine${i}`]) {
        this.linesInfo.controls[`nameLine${i}`].disable({ onlySelf: true, emitEvent: false });
        this.linesInfo.controls[`typeLine${i}`].disable({ onlySelf: true, emitEvent: false });
        this.linesInfo.controls[`callWaitingLine${i}`].disable({
          onlySelf: true,
          emitEvent: false
        });
      }
    }
  }

  private allLinesUnassigned(): boolean {
    return Object.keys(this.linesInfo.controls)
      .filter(key => key.includes("nameLine"))
      .every(key => this.linesInfo.controls[key].value === "");
  }

  private setFirstLineAssigned(i: number): void {
    if (i !== 1) {
      return;
    }
    this.firstLineAssigned = this.linesInfo.controls.nameLine1.value ? true : false;
  }

  private checkDuplicateLinesFormGroup() {
    const lineInfoValues = this.linesInfo.getRawValue();
    const lineInfoMap = new Map();
    for (let i = 1; i <= this.totalLines; i++) {
      // skip if line is empty
      if (!lineInfoValues[`nameLine${i}`]) {
        continue;
      }
      const lineInfoKey =
        lineInfoValues[`nameLine${i}`] +
        lineInfoValues[`typeLine${i}`] +
        lineInfoValues[`callWaitingLine${1}`];
      if (lineInfoMap.has(lineInfoKey)) {
        // set line to be in error and then break loop
        this.linesInfo.controls[`nameLine${i}`].setErrors({ duplicateKey: true });
        break;
      } else {
        lineInfoMap.set(lineInfoKey, true);
        // clear any old duplicated key
        // eslint-disable-next-line no-null/no-null
        this.linesInfo.controls[`nameLine${i}`].setErrors(null);
      }
    }
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }
}
